import { useCallback, ReactElement } from "react";
import { atom, useAtom, useSetAtom } from "jotai";
import { append, reject } from "ramda";

import { Toast, ToastProps } from "_/components/toast";
import { Uuid } from "_/types";

import * as S from "./styled";

// Data type as used by the Toasts component.
type ToastData = ToastProps & { id: Uuid };

// Data specification required when queueing a new toast.
type ToastSpec = Omit<ToastProps, "onClose">;

const toastsAtom = atom<ToastData[]>([]);

// Queue a toast without subscribing to the toast list. Prefer this over
// `useToasts` in components that only add toasts, so they don't re-render
// every time a toast is added or removed.
export function useAddToast(): (spec: ToastSpec) => void {
  const setToasts = useSetAtom(toastsAtom);

  // Memoized so callers can list `addToast` in hook dependency arrays
  // without the effect or callback being recreated on every render.
  return useCallback(
    (toast: ToastSpec) => {
      const id = crypto.randomUUID();
      const onClose = () => {
        setToasts((ts) => reject((t: ToastData) => t.id === id, ts));
      };

      setToasts((ts) => append({ id, onClose, ...toast }, ts));
    },
    [setToasts]
  );
}

export function useToasts(): [
  ToastData[],
  (spec: ToastSpec) => void,
  (id: Uuid) => void,
] {
  const [toasts, setToasts] = useAtom(toastsAtom);
  const addToast = useAddToast();

  const removeToast = useCallback(
    (id: Uuid) => {
      setToasts((ts) => reject((t: ToastData) => t.id === id, ts));
    },
    [setToasts]
  );

  return [toasts, addToast, removeToast];
}

export const Toasts = (): ReactElement => {
  const [toasts, _addToast, _removeToast] = useToasts();

  const toastEls = toasts.map((t: ToastData) => {
    const { id, ...rest } = t;

    return <Toast key={id} {...rest} />;
  });

  return <S.Container>{toastEls}</S.Container>;
};
